import { parseISO, format } from 'date-fns';
import type { JobOrder } from '@/lib/models/jobOrder';
import type { WorkerHolidayPerYear } from '@/lib/models/workerHolidayPerYear';
import { WorkerHolidayPerYearSchema } from '@/lib/models/workerHolidayPerYear';
import workerEndpoints from '@/lib/api/endpoints/workerEndpoints';
import type { Worker, HolidayStatus } from '@/lib/models/worker';
import { WorkerSchema, HolidayStatusSchema } from '@/lib/models/worker';
import type { WorkerIntegration } from '@/lib/models/workerIntegration';
import { WorkerIntegrationSchema } from '@/lib/models/workerIntegration';
import type { Holiday } from '@/lib/models/holiday';
import type { WorkingRelationship } from '@/lib/models/workingRelationship';
import { WorkingRelationshipSchema } from '@/lib/models/workingRelationship';
import type { ODataResponse } from '@/lib/models/common';
import { zodValidator } from '@/lib/utils/zodValidator';

/**
 * Interfaccia per il service Worker
 * Gestisce la logica di business e la validazione runtime per i lavoratori
 */
interface WorkersService {
	/**
	 * Recupera lista paginata di lavoratori
	 * @param query - Query string OData
	 * @returns Promise con ODataResponse di Worker
	 */
	getWorkers(query: string): Promise<ODataResponse<Worker>>;

	/**
	 * Recupera un singolo lavoratore per ID
	 * @param id - ID del lavoratore
	 * @returns Promise con Worker
	 */
	getWorker(id: number): Promise<Worker>;

	/**
	 * Crea un nuovo lavoratore
	 * @param payload - Dati del lavoratore
	 * @returns Promise con Worker creato
	 */
	postWorker(payload: Partial<Worker>): Promise<Worker>;

	/**
	 * Aggiorna un lavoratore esistente
	 * @param id - ID del lavoratore
	 * @param payload - Dati da aggiornare
	 * @returns Promise con Worker aggiornato
	 */
	updateWorker(id: number, payload: Partial<Worker>): Promise<Worker>;

	/**
	 * Elimina un lavoratore
	 * @param id - ID del lavoratore da eliminare
	 * @returns Promise void
	 */
	deleteWorker(id: number): Promise<void>;

	/**
	 * Recupera gli ordini gestiti dal lavoratore
	 * @param workerId - ID del lavoratore
	 * @returns Promise con array di JobOrder
	 */
	getManagedOrders(workerId: number): Promise<any[]>;

	/**
	 * Recupera gli ordini su cui può lavorare
	 * @param workerId - ID del lavoratore
	 * @returns Promise con array di JobOrder
	 */
	getWorkedOrders(workerId: number): Promise<any[]>;

	/**
	 * Recupera le ferie del lavoratore
	 * @param workerId - ID del lavoratore
	 * @returns Promise con array di WorkerHolidayPerYear
	 */
	getHolidays(workerId: number): Promise<any[]>;

	/**
	 * Recupera i rapporti di lavoro
	 * @param workerId - ID del lavoratore
	 * @returns Promise con array di WorkingRelationship
	 */
	getRelationships(workerId: number): Promise<WorkingRelationship[]>;

	/**
	 * Recupera lo status delle ferie per anno
	 * @param workerId - ID del lavoratore
	 * @param year - Anno di riferimento
	 * @returns Promise con HolidayStatus
	 */
	getHolidayStatus(workerId: number, year: number): Promise<HolidayStatus>;

	/**
	 * Recupera le integrazioni del lavoratore
	 * @param workerId - ID del lavoratore
	 * @returns Promise con array di WorkerIntegration (compatibilità con JS originale)
	 */
	getIntegrations(workerId: number): Promise<WorkerIntegration[]>;

	/**
	 * Genera export Excel dei lavoratori
	 * @param query - Query string per il filtro
	 * @returns Promise con Blob (file Excel)
	 */
	generateExcel(query: string): Promise<Blob>;

	/**
	 * Trova il rapporto di lavoro attivo per un lavoratore in una data specifica
	 * @param worker - Worker con i rapporti di lavoro caricati
	 * @param date - Data per cui cercare il rapporto attivo
	 * @returns WorkingRelationship attivo o null se non trovato
	 */
	getActiveRelationship(worker: Worker, date: Date): WorkingRelationship | null;

	/**
	 * Calcola le ore previste per un rapporto di lavoro in una data specifica
	 * @param workingRelationship - Rapporto di lavoro
	 * @param date - Data per il calcolo
	 * @param holidays - Lista delle festività
	 * @returns Numero di ore previste
	 */
	getExpectedHours(
		workingRelationship: WorkingRelationship,
		date: Date,
		holidays: Holiday[],
	): number;

	/**
	 * Verifica se una data è festiva (weekend + festività vere)
	 * @param date - Data da verificare
	 * @param holidays - Lista delle festività
	 * @param costCenterId - ID del centro di costo (opzionale)
	 * @returns true se la data è festiva
	 */
	isHoliday(date: Date, holidays: Holiday[], costCenterId?: number): boolean;

	/**
	 * Verifica se una data è una vera festività (esclusi weekend)
	 * @param date - Data da verificare
	 * @param holidays - Lista delle festività
	 * @param costCenterId - ID del centro di costo (opzionale)
	 * @returns true se la data è una vera festività
	 */
	isTrueHoliday(date: Date, holidays: Holiday[], costCenterId?: number): boolean;
}

/**
 * Implementazione del service Worker con validazione runtime
 */
const workersService: WorkersService = {
	async getWorkers(query: string): Promise<ODataResponse<Worker>> {
		const response = (await workerEndpoints.getWorkers(query)).data;
		// Validazione runtime della response (attiva solo in development)
		zodValidator.validateODataResponse(response, WorkerSchema, 'Worker.getAll');
		return response;
	},

	async getWorker(id: number): Promise<Worker> {
		const response = (await workerEndpoints.getWorker(id)).data;
		// Validazione runtime della response (attiva solo in development)
		zodValidator.validateSingle(response, WorkerSchema, 'Worker.getById');
		return response;
	},

	async postWorker(payload: Partial<Worker>): Promise<Worker> {
		// Validazione runtime del payload (attiva solo in development)
		// Usiamo partial per la creazione dato che alcuni campi vengono generati dal server
		const response = (await workerEndpoints.postWorker(payload)).data;
		// Validazione runtime della response (attiva solo in development)
		zodValidator.validateSingle(response, WorkerSchema, 'Worker.create.response');
		return response;
	},

	async updateWorker(id: number, payload: Partial<Worker>): Promise<Worker> {
		// Validazione runtime del payload (attiva solo in development)
		const response = (await workerEndpoints.updateWorker(id, payload)).data;
		// Validazione runtime della response (attiva solo in development)
		zodValidator.validateSingle(response, WorkerSchema, 'Worker.update.response');
		return response;
	},

	async deleteWorker(id: number): Promise<void> {
		return (await workerEndpoints.deleteWorker(id)).data;
	},

	async getManagedOrders(workerId: number): Promise<JobOrder[]> {
		const response = (await workerEndpoints.getManagedOrders(workerId)).data.value;
		return response;
	},

	async getWorkedOrders(workerId: number): Promise<any[]> {
		const response = (await workerEndpoints.getWorkedOrders(workerId)).data.value;
		// Restituisce JobOrderAllowedWorkers con expand jobOrder per CRUD
		return response;
	},

	async getHolidays(workerId: number): Promise<WorkerHolidayPerYear[]> {
		const response = (await workerEndpoints.getHolidays(workerId)).data.value;
		// Validazione array di WorkerHolidayPerYear
		zodValidator.validateArray(response, WorkerHolidayPerYearSchema, 'Worker.getHolidays');
		return response;
	},

	async getRelationships(workerId: number): Promise<WorkingRelationship[]> {
		const response = (await workerEndpoints.getRelationships(workerId)).data.value;
		// Validazione array di WorkingRelationship
		zodValidator.validateArray(response, WorkingRelationshipSchema, 'Worker.getRelationships');
		return response;
	},

	async getHolidayStatus(workerId: number, year: number): Promise<HolidayStatus> {
		const response = (await workerEndpoints.getHolidayStatus(workerId, year)).data;
		// Validazione runtime della response (attiva solo in development)
		zodValidator.validateSingle(response, HolidayStatusSchema, 'Worker.getHolidayStatus');
		return response;
	},

	async getIntegrations(workerId: number): Promise<WorkerIntegration[]> {
		const response = (await workerEndpoints.getIntegrations(workerId)).data;
		// Validazione runtime della response (attiva solo in development)
		zodValidator.validateODataResponse(
			response,
			WorkerIntegrationSchema,
			'WorkerIntegration.getAll',
		);
		// Restituisce l'array per compatibilità con il service JS originale
		return response.value || [];
	},

	async generateExcel(query: string): Promise<Blob> {
		return (await workerEndpoints.generateExcel(query)).data;
	},

	/**
	 * Trova il rapporto di lavoro attivo per un lavoratore in una data specifica
	 * Converte le date in formato numerico YYYYMMDD per il confronto
	 */
	getActiveRelationship(worker: Worker, date: Date): WorkingRelationship | null {
		if (!worker.workingRelationships || worker.workingRelationships.length === 0) {
			return null;
		}
		const activeRelationships = worker.workingRelationships.filter((relationship) => {
			const startDate = Number.parseInt(format(parseISO(relationship.startDate), 'yyyyMMdd'));
			const endDate = relationship.endDate
				? Number.parseInt(format(parseISO(relationship.endDate), 'yyyyMMdd'))
				: null;
			const comparisonDate = Number.parseInt(format(date, 'yyyyMMdd'));
			return startDate <= comparisonDate && (endDate === null || endDate > comparisonDate);
		});
		if (!activeRelationships || activeRelationships.length === 0) {
			return null;
		}
		return activeRelationships[0];
	},

	/**
	 * Calcola le ore previste in base al tipo di contratto
	 * Gestisce FullTime (8h), PartTime verticale e orizzontale
	 */
	getExpectedHours(
		workingRelationship: WorkingRelationship,
		date: Date,
		holidays: Holiday[],
	): number {
		if (this.isHoliday(date, holidays, workingRelationship.costCenterId)) {
			return 0;
		}
		let hours = 0;

		switch (workingRelationship.type) {
			case 1: // FullTime
				hours = 8;
				break;
			case 2: // PartTimeHorizontal
				hours = workingRelationship.hourPerDay || 0;
				break;
			case 3: // PartTimeVertical
				switch (date.getDay()) {
					case 1: // Lunedì
						hours = workingRelationship.mondayHours || 0;
						break;
					case 2: // Martedì
						hours = workingRelationship.tuesdayHours || 0;
						break;
					case 3: // Mercoledì
						hours = workingRelationship.wednesdayHours || 0;
						break;
					case 4: // Giovedì
						hours = workingRelationship.thursdayHours || 0;
						break;
					case 5: // Venerdì
						hours = workingRelationship.fridayHours || 0;
						break;
					case 6: // Sabato
						hours = workingRelationship.saturdayHours || 0;
						break;
					case 0: // Domenica (getDay() restituisce 0 per domenica)
						hours = workingRelationship.sundayHours || 0;
						break;
				}
				break;
		}
		return hours || 0;
	},

	/**
	 * Verifica se una data è una vera festività (esclusi weekend)
	 * Controlla se la festività si applica al centro di costo specificato
	 */
	isTrueHoliday(date: Date, holidays: Holiday[], costCenterId?: number): boolean {
		if (holidays && holidays.length > 0) {
			const found = holidays.filter(
				(h) => format(parseISO(h.holidayDate), 'yyyyMMdd') === format(date, 'yyyyMMdd'),
			);
			if (found.length > 0) {
				const holiday = found[0];
				if (!holiday.holidayCostCenters || holiday.holidayCostCenters.length === 0) {
					return true; // Festività generale (per tutti i centri di costo)
				}
				if (
					costCenterId &&
					holiday.holidayCostCenters &&
					holiday.holidayCostCenters.find((item) => item.costCenterId === costCenterId)
				) {
					return true; // Festività specifica per questo centro di costo
				}
				return false; // Festività per altri centri di costo
			}
		}
		return false;
	},

	/**
	 * Verifica se una data è festiva (weekend + festività vere)
	 * Considera festivi sabato (6) e domenica (0)
	 */
	isHoliday(date: Date, holidays: Holiday[], costCenterId?: number): boolean {
		if (date.getDay() === 0 || date.getDay() === 6) {
			// Domenica o Sabato
			return true;
		}
		return this.isTrueHoliday(date, holidays, costCenterId);
	},
};

export default workersService;
